import React from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Space } from "antd";

/**
 * Fallback when we have Router (nested under BrowserRouter): use SPA navigation + keys.
 */
function FallbackWithNavigate({ error, resetErrorBoundary }) {
  const navigate = useNavigate();
  return (
    <div className="route-error-boundary-fallback">
      <div className="route-error-boundary-inner">
        <h1 className="route-error-boundary-title">Something broke</h1>
        <p className="route-error-boundary-copy">
          This page hit an unexpected error. Try reloading, going home, or opening the browser
          console and filtering messages by <code>Ummahfy</code>.
        </p>
        <Alert
          type="error"
          message={error?.name || "Error"}
          description={
            <span className="route-error-boundary-technical">{error?.message}</span>
          }
          style={{ marginBottom: 24, textAlign: "left" }}
        />
        <Space wrap>
          <Button type="primary" onClick={resetErrorBoundary}>
            Reload this page
          </Button>
          <Button type="default" onClick={() => navigate("/")}>Go home</Button>
          <Button type="default" onClick={() => navigate("/events")}>Browse events</Button>
        </Space>
        <p className="route-error-boundary-hint muted">
          For event pages: open the console → type{" "}
          <code>
            sessionStorage.setItem(&apos;ummahfyDebugEvents&apos;,&apos;1&apos;)
          </code>{" "}
          → refresh.
        </p>
      </div>
    </div>
  );
}

/**
 * Route-scoped React error boundary. Resets automatically when routeKey changes
 * so navigating to another event clears a previous crash.
 *
 * Prefer children render-props reset so we reset state when resetting error.
 */
export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[Ummahfy RouteErrorBoundary]", error?.message || error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.routeKey !== this.props.routeKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  resetErrorBoundary = () => {
    this.setState({ error: null });
    window.scrollTo(0, 0);
  };

  render() {
    if (this.state.error) {
      if (typeof this.props.fallbackRender === "function") {
        return this.props.fallbackRender({
          error: this.state.error,
          resetErrorBoundary: this.resetErrorBoundary,
        });
      }
      return (
        <FallbackWithNavigate
          error={this.state.error}
          resetErrorBoundary={this.resetErrorBoundary}
        />
      );
    }
    return this.props.children;
  }
}
